/**
 * Visitor measurement for the public pages, without cookies, storage or personal data (31.10.2026,
 * docs/work/landing-optimise-2026-10-31.md).
 *
 * Two instruments, each answering a question the other cannot:
 *  1. Cloudflare Web Analytics, loaded on the public pages only: visits, page views, referrers,
 *     countries, Core Web Vitals. Cookieless. It has no custom events, so it cannot count a click.
 *  2. First-party counts: `record_site_event` (migration 20261031071310_site_events.sql) stores an
 *     event name, an optional detail word and the page language. No user id, no session id, no IP,
 *     no user agent, no URL: a row cannot be tied to a person or to another row. The funnel is read
 *     as ratios of totals (CTA clicks / landing views), never as journeys.
 *
 * Chose: Cloudflare Web Analytics plus counts in our own Supabase. Over: Plausible or Fathom (paid),
 * Google Analytics (cookies and personal data, needs a consent banner), a session id in
 * sessionStorage (true bounce rate, but it stores an identifier on the device). Because: free, no
 * consent banner, and the funnel numbers live next to the signups they lead to. Revisit if: traffic
 * is large enough that per-visit journeys would change a decision (then weigh a consented tool).
 *
 * Both instruments run only on the production host and never under automation
 * (`navigator.webdriver`), so builds, previews, CI and agents' own renders of the live page are not
 * counted as visitors.
 */
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/lib/constants'

export const PRODUCTION_HOST = 'eventripple.app'

/** Cloudflare Web Analytics site `6d9ab7db…` for eventripple.app. Public by design: it ships in the page. */
export const CLOUDFLARE_BEACON_TOKEN = 'adf29d92bae3481c9b7f18d1fd096c07'
export const CLOUDFLARE_BEACON_SRC = 'https://static.cloudflareinsights.com/beacon.min.js'

/** The only names the database accepts; site-metrics.test.ts holds this list equal to the migration's check. */
export const SITE_EVENT_NAMES = [
  'landing_view',
  'landing_section',
  'cta_click',
  'signup_view',
  'signup_start',
  'signup_created',
] as const
export type SiteEventName = (typeof SITE_EVENT_NAMES)[number]

/** One lowercase word, as the database's check constraint requires. */
const DETAIL = /^[a-z0-9_-]{1,32}$/

export function measuring(win: Pick<Window, 'location' | 'navigator'> = window): boolean {
  return win.location.hostname === PRODUCTION_HOST && !win.navigator.webdriver
}

function pageLanguage(language: string | undefined): SupportedLanguage | null {
  const base = (language ?? '').slice(0, 2)
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base) ? (base as SupportedLanguage) : null
}

/** The RPC's arguments: the name, a detail only if it is one lowercase word, the page language. Nothing else. */
export function siteEventBody(name: SiteEventName, detail: string | undefined, language: string | undefined) {
  return {
    p_name: name,
    p_detail: detail && DETAIL.test(detail) ? detail : null,
    p_lang: pageLanguage(language),
  }
}

/**
 * Counts one event. Fire and forget: measurement never blocks or breaks the page, so a failed call
 * is dropped silently. `keepalive` lets the request finish when the click leaves the page (Google
 * sign-in redirects away at once).
 */
export function recordSiteEvent(name: SiteEventName, detail?: string): void {
  if (typeof window === 'undefined' || !measuring()) return
  const url = import.meta.env.VITE_SUPABASE_URL
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!url || !key) return
  const body = JSON.stringify(siteEventBody(name, detail, document.documentElement.lang))
  try {
    void fetch(`${url}/rest/v1/rpc/record_site_event`, {
      method: 'POST',
      keepalive: true,
      headers: { apikey: key, Authorization: `Bearer ${key}`, 'Content-Type': 'application/json' },
      body,
    }).catch(() => {})
  } catch {
    // measurement is never allowed to throw into the page
  }
}

/**
 * The addresses a document that carries the Cloudflare beacon may ever show: the landing, the
 * PublicLayout pages, and the sign-up and sign-in steps a visitor takes from them. None carries an
 * id or a token. site-metrics.test.ts holds the PublicLayout part equal to the route list.
 *
 * Why a list (Pilot, #245, 31.10.2026): the beacon is a long-lived script, and `spa: false` only
 * stops it reporting client-side navigation. Its Web Vitals send, when the tab is hidden, reads the
 * address the tab is on at that moment (path kept, query and hash dropped), so a visitor who went
 * on from `/` into `/events/<id>/guests` in the same document sent that path. The rule that makes
 * this impossible: a beacon document only ever holds addresses on this list. It loads only in a
 * document that opened on one (`beaconAllowed`), and `PageBeaconBoundary` turns every navigation
 * off the list into a full page load, so the app always starts in a fresh document without it.
 */
export const BEACON_PATHS: readonly string[] = [
  '/',
  '/pricing',
  '/about',
  '/contact',
  '/privacy',
  '/terms',
  '/cookies',
  '/login',
  '/signup',
  '/forgot-password',
  '/signup-success',
]

export function isBeaconPath(pathname: string): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  return BEACON_PATHS.includes(path)
}

/** The address this document was opened on, before any client-side navigation. */
const OPENED_AT = typeof window === 'undefined' ? '' : window.location.href

function openedPath(win: Pick<Window, 'performance'>): string {
  const entry = win.performance?.getEntriesByType?.('navigation')[0]
  try {
    return new URL(entry?.name || OPENED_AT).pathname
  } catch {
    return ''
  }
}

/**
 * Whether this document may carry the beacon: measuring, opened on a listed address, and not
 * arrived at from one of our own unlisted addresses (the beacon reports `document.referrer`, and a
 * same-origin referrer carries the full previous address, query included).
 */
export function beaconAllowed(
  win: Pick<Window, 'location' | 'navigator' | 'performance'> & { document: Pick<Document, 'referrer'> } = window,
): boolean {
  if (!measuring(win) || !isBeaconPath(openedPath(win))) return false
  const referrer = win.document.referrer
  if (!referrer) return true
  try {
    const from = new URL(referrer)
    return from.origin !== win.location.origin || (isBeaconPath(from.pathname) && !from.search)
  } catch {
    return false
  }
}

/** Leaves this document for `href` by a full page load (`PageBeaconBoundary`). */
export function loadDocument(href: string, replace: boolean): void {
  if (replace) window.location.replace(href)
  else window.location.assign(href)
}

let beaconLoaded = false
const beaconListeners = new Set<() => void>()

/** For `useSyncExternalStore`: whether the beacon has been put on this document. */
export function isBeaconLoaded(): boolean {
  return beaconLoaded
}

export function subscribeBeacon(listener: () => void): () => void {
  beaconListeners.add(listener)
  return () => beaconListeners.delete(listener)
}

/**
 * Loads the Cloudflare beacon once, on a document `beaconAllowed` admits. `spa: false`: it reports
 * the page it was loaded on and does not follow client-side navigation; `BEACON_PATHS` and
 * `PageBeaconBoundary` keep the document from ever reaching an app address while it is loaded.
 */
export function loadPageBeacon(doc: Document = document): void {
  if (!beaconAllowed()) return
  if (!doc.querySelector(`script[src="${CLOUDFLARE_BEACON_SRC}"]`)) {
    const script = doc.createElement('script')
    script.defer = true
    script.src = CLOUDFLARE_BEACON_SRC
    script.setAttribute('data-cf-beacon', JSON.stringify({ token: CLOUDFLARE_BEACON_TOKEN, spa: false }))
    doc.head.appendChild(script)
  }
  if (beaconLoaded) return
  beaconLoaded = true
  beaconListeners.forEach((listener) => listener())
}
